<template>
  <div class="kanban">
    <!-- Plantilla de proceso de produccion -->
    <v-chip-group
      active-class="primary--text"
      class="mb-5 pb-3"
      column
      v-model="template"
    >
      <v-chip outlined :value="null">
        {{ $t('all') }}
      </v-chip>
      <v-chip
        outlined
        v-for="template in templates"
        :key="template.pk"
        :value="template.pk"
      >
        {{ template.template ? template.template + ' - ' : '' }}
        {{ template.name }}
      </v-chip>
    </v-chip-group>
    <v-row v-show="loadStatus" class="my-0">
      <v-col
        v-for="index in skeletonsCount"
        :key="'skeleton-' + index"
        cols="12"
        sm="6"
        md="3"
        lg="2"
      >
        <v-skeleton-loader type="article" class="mx-auto"></v-skeleton-loader>
      </v-col>
    </v-row>

    <!-- Estados de suborden -->
    <div v-show="!loadStatus" class="orders-status-container">
      <div v-for="(main_status, key) in main_statuses" :key="`status-${key}`">
        <orders-status
          :filter="main_status.pk"
          :preStatuses="statuses"
          :template="template"
          @quotation-event="getOrderStatus"
        />
      </div>
    </div>

    <!-- Modal para asignar diseñador -->
    <i-modal
      v-if="showAssignDesignerModal"
      dontSave
      max-width="800px"
      persistent
      :title="$tc('assignDesigner')"
      v-model="showAssignDesignerModal"
    >
      <div class="py-3">
        <assign-designer
          mode="designer"
          :selectedItem="product"
          :template="template"
          @wasAssigned="softUpdate"
        />
      </div>
    </i-modal>
    <!-- Modal para asignar productor (departamento) -->
    <i-modal
      v-if="showAssignProductionModal"
      dontSave
      max-width="800px"
      persistent
      :title="$tc('assignProducer')"
      v-model="showAssignProductionModal"
    >
      <div class="py-3">
        <assign-department
          :suborders="[theOrder]"
          mode="production"
          @updateAssignDepartment="softUpdate"
        />
      </div>
    </i-modal>

    <!-- Modal de email -->
    <dialChangeStatus
      :orderId="product"
      ref="dialChangeStatus"
    ></dialChangeStatus>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import OrdersStatus from '@/components/dashboard/status.vue'
import assignDesigner from '@/components/sale/infoAssignItem.vue'
import AssignDepartment from '@/components/sale/assignDepartment.vue'
import dialChangeStatus from '@/components/email/changeStatus.vue'

export default {
  components: {
    OrdersStatus,
    assignDesigner,
    AssignDepartment,
    dialChangeStatus
  },
  data() {
    return {
      theOrder: '',
      cartlist: [],
      templates: [],
      load: false,
      loading: false,
      loadStatus: false,
      template: null,
      statuses: [],
      main_statuses: [],
      newStatus: '',
      oldStatus: '',
      product: 0,
      showAssignDesignerModal: false, // Visibilidad del modal.
      showAssigUser: false, // Visibilidad del componente assignuser.
      showAssignProductionModal: false, // Visibilidad del modal.
      order: {
        mainStatus: 'designer'
      }
    }
  },
  computed: {
    /**
     * Determina si el usuario tiene los permisos necesarios según la indicación
     *
     * @return {boolean}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    checkPermissions() {
      return {
        isElevatedRole:
          this.getPermissions([`users.change_profile`]) &&
          this.getPermissions([`auth.change_user`]),
        isDesigner:
          this.getPermissions([`sales.change_assigneduser`]) &&
          this.getPermissions([`sales.view_assigneduser`]),
        isProducer:
          this.getPermissions([`sales.view_assigneddepartment`]) &&
          this.getPermissions([`sales.change_assigneddepartment`])
      }
    },
    /**
     * Devuelve una lista de números que representan los "skeletons".
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    skeletonsCount() {
      return [1, 2, 3, 4, 6, 7, 8, 9]
    },
    /**
     * Mapeo de getters de Vuex
     * Proporciona acceso a información de sesión y empresa.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    ...mapGetters({
      isAdmin: 'session/isAdmin',
      me: 'session/me',
      getPermissions: 'session/getPermissions',
      company: 'company/getCompanyData'
    })
  },
  watch: {
    /**
     * Observador para el cambio de la plantilla.
     * Llama a las funciones para obtener el estado de la orden.
     *
     * @param {Object} val - Valor actual de la plantilla.
     */
    template: {
      immediate: true,
      async handler() {
        await this.getMainOrderStatus()
        this.getOrderStatus()
      }
    }
  },
  methods: {
    ...mapActions({
      showConfirmation: 'confirmation/confirmationValue'
    }),
    /**
     * Obtiene la lista de productos asociados a la orden actual y la asigna a
     * `cartlist`. Si no hay productos en la orden y el estado principal de la
     * orden es "quote", muestra un mensaje de error al usuario.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getCartlist(pk) {
      const cart = await this.$api.sale.suborder.show({
        pk: pk,
        opt: { params: { order: true } }
      })
      this.cartlist = cart.data
    },
    /**
     * Maneja el evento de soltar un bloque, actualizando el estado del producto.
     *
     * @param {Object} block - Información del bloque soltado.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async handleDrop(block) {
      if (block.container === block.source) {
        return
      }

      // Obtener el producto y el nuevo estado
      this.product = Number(block.el.dataset.key)
      this.newStatus = Number(block.container.dataset.status)
      const previous_status_number = Number(block.source.dataset.status)

      // Verificar si hay estados disponibles
      if (this.statuses.length > 0) {
        const order = this.statuses.find((item) => item.pk === this.newStatus)
        const previousOrderStatus = this.statuses.find(
          (item) => item.pk === previous_status_number
        )
        this.theOrder = order

        if (order) {
          const name = this.extractLastWord(order.name)

          const newOrderStatus = this.getNewOrderStatus(name)

          this.form = {
            product: this.product,
            newOrderStatus: newOrderStatus
          }

          if (
            order.name == 'design - in_process' ||
            order.name == 'design_in_process'
          ) {
            this.$api.suborder.assigneduser
              .show({
                pk: this.product,
                opt: {
                  params: {
                    mode: 'designer'
                  }
                }
              })
              .then((response) => {
                if (response.data.length == 0) {
                  this.showAssignDesignerModal = true
                  return
                } else {
                  this.handleUpdateInfo()
                  return
                }
              })
            return
          } else if (name == 'in_design') {
            this.$api.suborder.assigneduser
              .show({
                pk: this.product,
                opt: {
                  params: {
                    mode: 'designer'
                  }
                }
              })
              .then((response) => {
                if (response.data.length == 0) {
                  this.showAssignDesignerModal = true
                  return
                } else {
                  this.handleUpdateInfo()
                  return
                }
              })
            return
          } else if (
            order.name.includes('production') &&
            !order.name.endsWith('in_wait')
          ) {
            this.$api.suborder.show({ pk: this.product }).then((response) => {
              this.theOrder = response.data
              if (this.theOrder.orderProductDepartment.length === 0) {
                this.showAssignProductionModal = true
              } else {
                this.handleUpdateInfo()
              }
            })
            return
            // Si se retrocede de diseño en revisión  a diseño en espera =>
          } else if (
            previousOrderStatus.name === 'design_under_review - in_wait' &&
            order.name === 'design - in_wait'
          ) {
            this.showConfirmation({
              show: true,
              type: 'primary',
              title: this.$tc('change', 2),
              model: this.$t('designer'),
              content: this.$t('confirm_change_designer'),
              alert: `${this.$t('success', {
                model: this.$t('designer'),
                action: this.$tc('change', 1)
              })}`,
              confirm: () => {
                this.handleYesDesigner()
              },
              cancel: () => {
                this.handleNoDesigner()
              }
            })
          } else {
            this.handleUpdateInfo()
          }
        }
      }
    },
    softUpdate() {
      this.$api.dashboard
        .edit({ pk: this.newStatus, form: this.form })
        .then((response) => {
          if (response.data && response.data.emailTemplate) {
            this.$store.dispatch(
              'utils/openDialogEmailNotification',
              response.data.emailTemplate
            )
          }
          this.$nextTick(() => {
            this.getOrderStatus()
          })
        })
    },
    handleUpdateInfo() {
      this.$api.dashboard
        .edit({ pk: this.newStatus, form: this.form })
        .then((response) => {
          if (response.data && response.data.emailTemplate) {
            this.$store.dispatch(
              'utils/openDialogEmailNotification',
              response.data.emailTemplate
            )
          }
          this.$nextTick(() => {
            this.getOrderStatus()
          })
        })
        .catch(async (error) => {
          this.templates = []
          this.statuses = []
          this.main_statuses = []
          const previousTemplate = this.template
          await this.getTemplates()
          await this.getMainOrderStatus()
          this.getOrderStatus()
          setTimeout(() => {
            this.template = previousTemplate
          }, 500)

          // Capturar el error
          if (error.response && error.response.data) {
            // Verificación segura de la propiedad orderProduct
            if (
              this.theOrder &&
              this.theOrder.products &&
              this.theOrder.products.length > 0
            ) {
              // Llamar a getCartlist y filtrar la orden por el ID.
              this.getCartlist(this.theOrder.products[0].orderProduct)

              // Mostrar el modal
              //this.showAssignDesignerModal = true
            }
          } else {
            console.error('Error:', error)
          }
        })
    },
    /**
     * Maneja la respuesta del usuario en el modal.
     *
     * @param {boolean} assign - Indica si el usuario seleccionó "Sí" (true) o
     * "No" (false).
     *
     * @author Ing. Argenis Osorio (argenisosorio10 at gmail)
     */
    handleModalResponse(assign) {
      if (assign) {
        this.showAssigUser = true
      } else {
        this.closeModal()
      }
    },
    /**
     * Cierra el modal de asignación de diseñador.
     *
     * @author Ing. Argenis Osorio (argenisosorio10 at gmail)
     */
    closeModal() {
      this.showAssigUser = false
      this.showAssignDesignerModal = false
    },
    /**
     * Obtiene el estado anterior basado en el código del producto.
     *
     * @returns {String} Nombre del estado anterior o una cadena vacía si no se
     *                   encuentra.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    getOldStatus() {
      return (
        this.statuses.find((status) =>
          status.products.some((item) => item.code === this.email['code'])
        )?.name || ''
      )
    },
    /**
     * Determina el estado de preproceso, basado en el nombre del estado.
     *
     * @param {String} name - Nombre del estado actual.
     * @returns {String} Estado de preproceso correspondiente.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    getNewOrderStatus(name) {
      const statusMap = {
        quote: 'quote',
        sended_quote: 'sended_quote',
        canceled: 'canceled',
        at_home: 'delivery',
        pickup: 'delivery'
      }

      return statusMap[name] ?? 'in_process'
    },
    /**
     * Extrae el estado en preproceso.
     *
     * @param {String} string - Cadena de la cual extraer la última palabra.
     * @returns {String} Última palabra de la cadena.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    extractLastWord(string) {
      const words = string.split(' ')
      return words[words.length - 1] || ''
    },
    /**
     * Extrae la primera letra de la orden
     * @param string
     */
    extractFirstWord(string) {
      const words = string.split(' ')
      return words[0] || ''
    },
    /**
     * Obtiene el estado del pedido para cada producto en el carrito.
     * Actualiza el estado en el dashboard.
     *
     * @param {String} state - Estado a verificar.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getOrderStatus() {
      try {
        this.loadStatus = true
        const canViewAll =
          this.isAdmin ||
          this.checkPermissions.isElevatedRole ||
          this.checkPermissions.isDesigner ||
          this.checkPermissions.isProducer

        let params = {
          opt: {
            params: {
              user: canViewAll ? null : this.me.id,
              company: this.company.pk
            }
          }
        }

        if (this.template) {
          params.pk = this.template
        }

        const res = await this.$api.dashboard.list(params)
        this.statuses = res.data
      } catch (error) {
        console.error('Error fetching order status:', error)
      } finally {
        this.loadStatus = false
      }
    },
    /**
     * Obtiene el estado principal de la orden.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getMainOrderStatus() {
      try {
        this.load = true
        const response = await this.$api.sale.status.list({
          opt: {
            params: {
              template: this.template,
              company: this.company.pk
            }
          }
        })

        if (this.checkPermissions.isElevatedRole) {
          this.main_statuses = response.data
        } else {
          if (this.checkPermissions.isDesigner) {
            this.main_statuses = response.data.filter(
              (status) => status.name === 'design'
            )
          }
          if (this.checkPermissions.isProducer) {
            this.main_statuses = response.data.filter(
              (status) => status.name === 'production'
            )
          }
        }
      } catch (error) {
        console.error('Error fetching main order status:', error)
      } finally {
        this.load = false
      }
    },
    /**
     * Obtiene las plantillas de proceso de producción disponibles.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getTemplates() {
      try {
        this.loading = true
        const response = await this.$api.product.template.list({
          opt: {
            params: {
              order: true,
              company: this.company.pk,
              has_orders: true
            }
          }
        })
        this.templates = response.data.filter((item) => item.orderNumber > 0)
      } catch (error) {
        console.error('Error fetching templates:', error)
      } finally {
        this.loading = false
      }
    },
    handleYesDesigner() {
      this.showAssignDesignerModal = true
      return
    },
    handleNoDesigner() {
      this.handleUpdateInfo()
      return
    }
  },
  created() {
    const service = this.$dragula.$service
    service.eventBus.$on('drop', this.handleDrop)
  },
  beforeDestroy() {
    const service = this.$dragula.$service
    service.eventBus.$off('drop', this.handleDrop)
  },
  mounted() {
    this.getTemplates()
  }
}
</script>
<style lang="sass">
.orders-status-container
  display: flex
  flex-direction: column
  gap: 10px
  padding-bottom: 10px
  @media (width >= 600px)
    flex-direction: row
    width: 100%
    overflow-x: auto
</style>
